import * as Sentry from "@sentry/react";
import { useAtomValue } from "jotai";
import { useCallback, useRef } from "react";
import { useAccountQuery } from "../../../api/features/account/use-account.query";
import { useUserSubscriptionTierQuery } from "../../../api/features/user-subscription/useUserSubscriptionTier.query";
import { useIsClara2Available } from "../../../hooks/feature-flags/useIsClara2Available/useIsClara2Available";
import { chatbotSessionService } from "../../../legacy/components/Chatbot/lib/services/chatbot-session/chatbot-session.service.instance";
import {
  BONUSX_SUBSCRIPTION_TIERS,
  GENERAL_SUBSCRIPTION_TO_BONUSX_SUBSCRIPTION_TIER_MAP,
} from "../../../legacy/services/user-subscription/enums/bonusx-subscription-tiers.enum";
import { trackClaraConversationStarted } from "../../../lib/mixpanel/track-clara-conversation-started";
import { claraEntryPointAtom } from "../store/clara-entry-point.store";
import type { IUseTrackClaraConversationStarted } from "./useTrackClaraConversationStarted.interface";

export function useTrackClaraConversationStarted(): IUseTrackClaraConversationStarted {
  const { accountData } = useAccountQuery();
  const { currentUserTier } = useUserSubscriptionTierQuery();
  const entryPoint = useAtomValue(claraEntryPointAtom);
  const isClara2Available = useIsClara2Available();

  const eventContextRef = useRef({ accountData, currentUserTier, entryPoint, isClara2Available });
  eventContextRef.current = { accountData, currentUserTier, entryPoint, isClara2Available };

  const trackConversationStarted = useCallback(async function trackConversationStarted(): Promise<void> {
    const eventContext = eventContextRef.current;
    if (!eventContext.isClara2Available) {
      return;
    }

    try {
      const conversationId = await chatbotSessionService.getActiveSessionId();
      if (!conversationId) {
        return;
      }

      const userTierAtEvent =
        GENERAL_SUBSCRIPTION_TO_BONUSX_SUBSCRIPTION_TIER_MAP[eventContext.currentUserTier] ??
        BONUSX_SUBSCRIPTION_TIERS.FREE;

      trackClaraConversationStarted({
        accountId: eventContext.accountData?.id,
        email: eventContext.accountData?.email,
        conversationId,
        userTierAtEvent,
        entryPoint: eventContext.entryPoint,
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  }, []);

  return { trackConversationStarted };
}
